import {
  DEFAULT_NOTIFICATION_TITLE_MESSAGES,
  getNotificationTitle,
} from '@/hooks/useNotificationTitle';
import { getIntlRef } from '@/lib/intl';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import React from 'react';
import { ToastContainer, cssTransition, toast } from 'react-toastify';
import { clsx } from '../../utils/clsx';
import Button from '../Button/Button';
import './Notification.css';

const ICONS = {
  success: <CheckCircle2 size={18} strokeWidth={1.75} />,
  warning: <AlertTriangle size={18} strokeWidth={1.75} />,
  error: <XCircle size={18} strokeWidth={1.75} />,
  info: <Info size={18} strokeWidth={1.75} />,
};

const TYPE_MAP = {
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'info',
};

const compactFadeTransition = cssTransition({
  enter: 'ds-notification-fade-enter',
  exit: 'ds-notification-fade-exit',
  collapseDuration: 180,
});

const resolveVariant = (variant) => TYPE_MAP[variant] || 'info';

const NotificationCloseButton = ({ closeToast, className, ariaLabel }) => (
  <Button
    size="small"
    type='text'
    icon={<X size={12} style={{ color: 'var(--danger)' }} />}
    aria-label={ariaLabel || 'close'}
    className={clsx('ds-notification__close', className)}
    onClick={(event) => {
      event.stopPropagation();
      closeToast();
    }}
  />
);

const DEFAULT_TITLE = DEFAULT_NOTIFICATION_TITLE_MESSAGES.general;

const resolveTitle = ({ title, context, titleId, titleDefaultMessage }) => {
  if (title) {
    return title;
  }

  const intl = getIntlRef();
  const contextId = typeof context === 'string' && context.trim() ? context.trim() : null;
  const resolvedDefault =
    titleDefaultMessage ||
    (contextId ? DEFAULT_NOTIFICATION_TITLE_MESSAGES[contextId] : DEFAULT_TITLE) ||
    DEFAULT_TITLE;

  if (!intl || typeof intl.formatMessage !== 'function') {
    return resolvedDefault;
  }

  if (titleId) {
    return intl.formatMessage({ id: titleId, defaultMessage: resolvedDefault });
  }

  if (contextId) {
    try {
      return getNotificationTitle(intl, contextId);
    } catch (error) {
      console.warn('[Notification] Failed to resolve title', error);
      return resolvedDefault;
    }
  }

  return resolvedDefault;
};

export const showNotification = (message, options = {}, extras) => {
  let normalized;

  if (typeof message === 'object' && !React.isValidElement(message)) {
    normalized = { ...message };
  } else if (typeof options === 'string') {
    normalized = { message, variant: options };
  } else if (options && typeof options === 'object' && !Array.isArray(options)) {
    normalized = { message, ...options };
  } else {
    normalized = { message };
  }

  if (typeof extras === 'string') {
    normalized = { ...normalized, context: extras };
  } else if (extras && typeof extras === 'object' && !Array.isArray(extras)) {
    normalized = { ...normalized, ...extras };
  }

  const {
    message: body,
    title,
    context,
    titleId,
    titleDefaultMessage,
    hideTitle = false,
    variant = 'info',
    autoClose = 5000,
    id,
    action,
    compact = false,
    position,
    replace = false,
  } = normalized;

  const type = resolveVariant(variant);
  const icon = ICONS[type] || ICONS.info;
  const resolvedTitle =
    hideTitle || compact ? null : resolveTitle({ title, context, titleId, titleDefaultMessage });

  if (id && replace) {
    toast.dismiss(id);
  }

  return toast(
    ({ closeToast }) => (
      <div
        className="ds-notification__content"
        role={compact ? 'status' : 'alert'}
        aria-live={compact ? 'polite' : undefined}
      >
        <span className={clsx('ds-notification__icon', `ds-notification__icon--${type}`)}>{icon}</span>
        <div className="ds-notification__text">
          {resolvedTitle ? <div className="ds-notification__title">{resolvedTitle}</div> : null}
          {body ? <div className="ds-notification__message">{body}</div> : null}
          {action ? (
            <Button
              type="button"
              className="ds-notification__action"
              onClick={() => {
                action.onClick?.();
                closeToast();
              }}
            >
              {action.label}
            </Button>
          ) : null}
        </div>
      </div>
    ),
    {
      toastId: id,
      type,
      autoClose,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnFocusLoss: false,
      pauseOnHover: !compact,
      draggable: false,
      className: clsx(
        'ds-notification',
        `ds-notification--${type}`,
        compact && 'ds-notification--compact',
      ),
      bodyClassName: 'ds-notification__body',
      closeButton: compact ? false : NotificationCloseButton,
      position,
      transition: compact ? compactFadeTransition : undefined,
    },
  );
};

export const NotificationHost = (props) => (
  <ToastContainer
    position="bottom-right"
    newestOnTop
    closeOnClick
    hideProgressBar
    draggable={false}
    limit={5}
    icon={false}
    closeButton={NotificationCloseButton}
    {...props}
    toastClassName={(context) =>
      clsx('ds-notification', `ds-notification--${resolveVariant(context?.type)}`)
    }
    bodyClassName="ds-notification__body"
  />
);

export default NotificationHost;
